/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import InboundCallsWidget from "../widgets/call-overview/InboundCallsWidget";
import InboundDirectCallsWidget from "../widgets/call-overview/InboundDirectCallsWidget.tsx";
import InternalCallsWidget from "../widgets/call-overview/InternalCallsWidget";
import OutboundCallsWidget from "../widgets/call-overview/OutboundCallsWidget";
import AbandonedRateWidget from "../widgets/call-overview/AbandonedRateWidget";
import TransferredRateWidget from "../widgets/call-overview/TransferredRateWidget";
import CallVolumeGroupWidget from "../widgets/call-overview/CallVolumeGroupWidget.tsx";
import { DateRangeFilter, DateRangeFilterChange } from "../filters/DateRangeFilter.tsx";
import { calculateDateRange } from "@/utils/dateRangeUtils";
import { getSharedDateRange, setSharedDateRange } from "@/utils/generalDateRangeStore";
import { WIDGET_NAMES } from "@/constants/widget";
import CallStatusDistributionTrendWidget from "../widgets/key-metrics/CallStatusDistributionTrendWidget";
import { WidgetType } from "@/types";
import { fetchKeyMetricWidget, fetchKeyMetricsBulk } from "@/lib/axios/api/widgets.api";
import { useAppData } from "@/contexts/AppDataContext";
import SLAWidget from "../widgets/call-overview/SLAWidget.tsx";
import TotalMissedCallsWidget from "../widgets/call-overview/TotalMissedCallsWidget.tsx";
import { WIDGET_IDS } from "@/constants/widget";

interface KeyMetricsTabProps {
  isGeneralMode: boolean;
}

type AgentRef = { microsoftId: string };
type QueueItem = { type: string; microsoftId: string; agents?: AgentRef[] };

type KPI<T extends string, V extends string> = { [K in T]?: { [KV in V]: number } & { percentage: number | null } | null };
type CallVolumeRow = { time: Date; inboundCall: number; outboundCall: number; inboundDirectCall?: number; internalCall?: number };
type KeyMetricsPartial =
  KPI<"totalInbound", "totalInboundCalls">
  & KPI<"totalOutbound", "totalOutboundCalls">
  & KPI<"totalInboundDirect", "totalInboundDirectCalls">
  & KPI<"totalInternal", "totalInternalCalls">
  & KPI<"totalMissed", "totalMissedCalls">
  & { sla?: { slaRate: number; percentage: number | null } | null }
  & { callAbandoned?: { callAbandonedRate: number; percentage: number | null } | null }
  & { transferred?: { transferredRate: number; percentage: number | null } | null }
  & { callVolume?: CallVolumeRow[] }
  & { callStatusDistributionTrend?: unknown[] };

const INITIAL_WIDGET_LOADING: Record<string, boolean> = [
  WIDGET_NAMES.TOTAL_INBOUND_CALLS,
  WIDGET_NAMES.TOTAL_OUTBOUND_CALLS,
  WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS,
  WIDGET_NAMES.TOTAL_INTERNAL_CALLS,
  WIDGET_NAMES.TOTAL_MISSED_CALLS,
  WIDGET_NAMES.SLA,
  WIDGET_NAMES.CALL_ABANDONED_RATE,
  WIDGET_NAMES.TRANSFERRED_RATE,
  WIDGET_NAMES.CALL_VOLUME,
  WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND,
].reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {} as Record<string, boolean>);

const KeyMetricsTab: React.FC<KeyMetricsTabProps> = ({ isGeneralMode }) => {
  const { queuesResponse } = useAppData();
  const [dateRange, setDateRange] = React.useState<DateRangeFilterChange>(getSharedDateRange);
  const updateDateRange = (value: DateRangeFilterChange) => {
    setDateRange(value);
    setSharedDateRange(value);
  };
  const [data, fetchData] = React.useState<KeyMetricsPartial>({});
  const [loadingMap, setLoadingMap] = React.useState<Record<string, boolean>>(INITIAL_WIDGET_LOADING);
  const [errorMap, setErrorMap] = React.useState<Record<string, string | null>>({});
  const [timeStamp, setTimeStamp] = React.useState(() => new Date().toISOString());

  // Refresh data when tab becomes active (same pattern as CallOverviewSection)
  React.useEffect(() => {
    if (isGeneralMode) {
      setTimeStamp(new Date().toISOString());
    }
  }, [isGeneralMode]);

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    if (queuesResponse && queuesResponse.length > 0) {
      // Calculate startDate: first day of this month - 6 months
      const endDate = new Date();
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 5, 1);
      // Compute queueIds, agentIds, directAgentIds from queuesResponse
      const queueIds = (queuesResponse as QueueItem[])
        .filter((item) => item.type === "CallQueue")
        .map((item) => item.microsoftId);
      const agentIds = Array.from(new Set((queuesResponse as QueueItem[])
        .filter((item) => item.type === "CallQueue")
        .flatMap((item) => (item.agents || []).map((agent) => agent.microsoftId))));
      const directAgentIds = Array.from(new Set((queuesResponse as QueueItem[])
        .filter((item) => item.type === "DirectAgentGroup")
        .flatMap((item) => (item.agents || []).map((agent) => agent.microsoftId))));

      const basePayload = {
        start: dateRange.startDate.toISOString(),
        end: dateRange.endDate.toISOString(),
        previousStart: dateRange.previousStartDate.toISOString(),
        previousEnd: dateRange.previousEndDate.toISOString(),
        statusTrendStart: startDate.toISOString(),
        statusTrendEnd: endDate.toISOString(),
        offsetInMinutes: -new Date().getTimezoneOffset(),
        queueIds,
        agentIds,
        directAgentIds
      };

      const setL = (k: string, v: boolean) => isMounted && setLoadingMap(prev => ({ ...prev, [k]: v }));
      const setE = (k: string, v: string | null) => isMounted && setErrorMap(prev => ({ ...prev, [k]: v }));
      const setD = (key: string, value: unknown) => isMounted && fetchData((prev) => ({ ...prev, [key]: value } as KeyMetricsPartial));

      const mapWidgetToKey: Record<number, string> = {
        [WIDGET_IDS.TOTAL_INBOUND_CALLS]: "totalInbound",
        [WIDGET_IDS.TOTAL_OUTBOUND_CALLS]: "totalOutbound",
        [WIDGET_IDS.TOTAL_INBOUND_DIRECT_CALLS]: "totalInboundDirect",
        [WIDGET_IDS.TOTAL_INTERNAL_CALLS]: "totalInternal",
        [WIDGET_IDS.TOTAL_INBOUND_CALLS_MISSED]: "totalMissed",
        [WIDGET_IDS.SLA]: "sla",
        [WIDGET_IDS.CALL_ABANDONED_RATE]: "callAbandoned",
        [WIDGET_IDS.TRANSFERRED_RATE]: "transferred",
        [WIDGET_IDS.CALL_VOLUME]: "callVolume",
        [WIDGET_IDS.CALL_STATUS_DISTRIBUTION_TREND]: "callStatusDistributionTrend",
      };

      // Map enum names to widget IDs (for backward compatibility with API responses)
      const enumNameToWidgetId: Record<string, number> = {
        "TotalInboundCalls": WIDGET_IDS.TOTAL_INBOUND_CALLS,
        "TotalOutboundCalls": WIDGET_IDS.TOTAL_OUTBOUND_CALLS,
        "TotalInboundDirectCalls": WIDGET_IDS.TOTAL_INBOUND_DIRECT_CALLS,
        "TotalInternalCalls": WIDGET_IDS.TOTAL_INTERNAL_CALLS,
        "TotalInboundCallsMissed": WIDGET_IDS.TOTAL_INBOUND_CALLS_MISSED,
        "SLA": WIDGET_IDS.SLA,
        "CallAbandonedRate": WIDGET_IDS.CALL_ABANDONED_RATE,
        "TransferredRate": WIDGET_IDS.TRANSFERRED_RATE,
        "CallVolume": WIDGET_IDS.CALL_VOLUME,
        "CallStatusDistributionTrend": WIDGET_IDS.CALL_STATUS_DISTRIBUTION_TREND,
      };

      const kpiWidgets: number[] = [
        WIDGET_IDS.TOTAL_INBOUND_CALLS,
        WIDGET_IDS.TOTAL_OUTBOUND_CALLS,
        WIDGET_IDS.TOTAL_INBOUND_DIRECT_CALLS,
        WIDGET_IDS.TOTAL_INTERNAL_CALLS,
        WIDGET_IDS.TOTAL_INBOUND_CALLS_MISSED,
        WIDGET_IDS.SLA,
        WIDGET_IDS.CALL_ABANDONED_RATE,
        WIDGET_IDS.TRANSFERRED_RATE
      ];
      const kpiNames = [
        WIDGET_NAMES.TOTAL_INBOUND_CALLS,
        WIDGET_NAMES.TOTAL_OUTBOUND_CALLS,
        WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS,
        WIDGET_NAMES.TOTAL_INTERNAL_CALLS,
        WIDGET_NAMES.TOTAL_MISSED_CALLS,
        WIDGET_NAMES.SLA,
        WIDGET_NAMES.CALL_ABANDONED_RATE,
        WIDGET_NAMES.TRANSFERRED_RATE
      ];
      const chartWidgets: number[] = [
        WIDGET_IDS.CALL_VOLUME,
        WIDGET_IDS.CALL_STATUS_DISTRIBUTION_TREND
      ];
      const chartNames = [
        WIDGET_NAMES.CALL_VOLUME,
        WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND
      ];

      // mark loading and clear errors
      kpiNames.forEach(n => setL(n, true));
      chartNames.forEach(n => setL(n, true));
      kpiNames.forEach(n => setE(n, null));
      chartNames.forEach(n => setE(n, null));

      (async () => {
        try {
          const kpiRes = await fetchKeyMetricsBulk(basePayload, kpiWidgets, { signal: controller.signal });
          Object.entries(kpiRes || {}).forEach(([k, v]) => {
            // Try parsing as number first, if NaN then try enum name mapping
            const parsed = Number(k);
            const widgetId = Number.isNaN(parsed) ? enumNameToWidgetId[k] : parsed;
            const dest = mapWidgetToKey[widgetId];
            if (dest) setD(dest, v);
          });
        } catch (err: any) {
          if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
            kpiNames.forEach(n => setE(n, err?.message || "Failed to load data"));
          }
        } finally {
          kpiNames.forEach(n => setL(n, false));
        }

        try {
          const chartRes = await fetchKeyMetricsBulk(basePayload, chartWidgets, { signal: controller.signal });
          Object.entries(chartRes || {}).forEach(([k, v]) => {
            // Try parsing as number first, if NaN then try enum name mapping
            const parsed = Number(k);
            const widgetId = Number.isNaN(parsed) ? enumNameToWidgetId[k] : parsed;
            const dest = mapWidgetToKey[widgetId];
            if (dest) setD(dest, v);
          });
        } catch (err: any) {
          if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
            chartNames.forEach(n => setE(n, err?.message || "Failed to load data"));
          }
        } finally {
          chartNames.forEach(n => setL(n, false));
        }
      })();
    }
    return () => { isMounted = false; controller.abort(); };
  }, [dateRange, queuesResponse, timeStamp]);

  const renderWidget = (widgetId: string, WidgetComponent: React.ComponentType<any>, data?: any) => {
    return (
      <WidgetComponent
        key={widgetId}
        data={{
          ...data
        }}
        isGeneralMode={true}
        loading={!!loadingMap[widgetId]}
        error={errorMap[widgetId] || null}
      />
    );
  };

  if (!isGeneralMode) return null;

  return (
    <div className="w-full">
      <div className="border text-primary bg-[#FCFCFD] rounded-lg dark:border-[#3C5C73] dark:bg-[#3C5C73] dark:text-white" style={{ padding: "8px" }} >
        <div className="mb-4 flex flex-col md:flex-row align-center gap-2">
          <DateRangeFilter
            value={dateRange.key}
            onChange={updateDateRange}
            initialDateRange={dateRange as any}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {renderWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS, InboundCallsWidget, {
            value: data?.totalInbound?.totalInboundCalls,
            comparisonPercentage: data?.totalInbound?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS, OutboundCallsWidget, {
            value: data?.totalOutbound?.totalOutboundCalls,
            comparisonPercentage: data?.totalOutbound?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS, InboundDirectCallsWidget, {
            value: data?.totalInboundDirect?.totalInboundDirectCalls,
            comparisonPercentage: data?.totalInboundDirect?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_INTERNAL_CALLS, InternalCallsWidget, {
            value: data?.totalInternal?.totalInternalCalls,
            comparisonPercentage: data?.totalInternal?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_MISSED_CALLS, TotalMissedCallsWidget, {
            value: data?.totalMissed?.totalMissedCalls,
            comparisonPercentage: data?.totalMissed?.percentage
          })}
          {renderWidget(WIDGET_NAMES.SLA, SLAWidget, {
            value: data?.sla?.slaRate,
            comparisonPercentage: data?.sla?.percentage
          })}
          {renderWidget(WIDGET_NAMES.CALL_ABANDONED_RATE, AbandonedRateWidget, {
            value: data?.callAbandoned?.callAbandonedRate,
            comparisonPercentage: data?.callAbandoned?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TRANSFERRED_RATE, TransferredRateWidget, {
            value: data?.transferred?.transferredRate,
            comparisonPercentage: data?.transferred?.percentage
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: `calc(100vh - 460px)` }}>
          <CallVolumeGroupWidget
            data={data?.callVolume || []}
            dateRange={{ from: new Date(dateRange.startDate), to: new Date(dateRange.endDate) }}
            loading={!!loadingMap[WIDGET_NAMES.CALL_VOLUME]}
          />
          <CallStatusDistributionTrendWidget
            data={{
              id: "call-status-distribution-trend",
              type: WidgetType.INBOUND_STATUS_DISTRIBUTION,
              title: "Call Status Distribution Trend",
              value: data?.callStatusDistributionTrend || [],
            }}
            loading={!!loadingMap[WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND]}
            error={errorMap[WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND] || null}
            rangeType="monthly"
          />
        </div>
      </div>
    </div>
  );
};

export default KeyMetricsTab;
